import { useEffect, useState } from 'react';
import { useIsAuthenticated } from 'react-auth-kit';
import { useApi } from '../components/home/apiService';

// The NavBar is mounted per-page, so an uncached fetch would hit
// /accounts/me/plan on every navigation. Cache the derived answer at module
// level: one request per SPA session, shared across all mounts.
type PlanStatus = { isFreeTier: boolean };

let cached: PlanStatus | null = null;
let inFlight: Promise<PlanStatus> | null = null;

// Full-page redirects (Stripe checkout/portal round-trips) reset the cache for
// free; this covers same-session plan flips (BillingReturn's activation poll).
export const invalidatePlanStatusCache = () => {
  cached = null;
  inFlight = null;
};

export interface UsePlanStatusReturn {
  // True when the user is on a free plan with a paid plan available to buy —
  // i.e. an upgrade CTA makes sense. Fails closed (false) on any error.
  isFreeTier: boolean;
  loading: boolean;
}

export const usePlanStatus = (): UsePlanStatusReturn => {
  const isAuthenticated = useIsAuthenticated();
  const api = useApi();
  const [status, setStatus] = useState<PlanStatus | null>(cached);

  useEffect(() => {
    if (!isAuthenticated() || cached) return;
    let cancelled = false;
    if (!inFlight) {
      inFlight = api
        .fetchMyPlan()
        .then(({ current, selectable, subscriptionStatus, hasBilling, pendingRequest }) => {
          const hasActiveStripeSub =
            hasBilling && ['active', 'trialing', 'past_due'].includes(subscriptionStatus ?? '');
          return {
            isFreeTier:
              !current?.stripePriceId &&
              !hasActiveStripeSub &&
              selectable.some((p) => p.stripePriceId) &&
              !pendingRequest,
          };
        })
        .catch(() => ({ isFreeTier: false }));
    }
    inFlight.then((result) => {
      cached = result;
      if (!cancelled) setStatus(result);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isFreeTier: status?.isFreeTier ?? false, loading: status === null };
};
